import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

interface HeroSectionProps {
  badge?: {
    icon?: React.ReactNode;
    text: string;
  };
  headline: string;
  highlightedText?: string;
  subheadline: string;
  primaryCTA: {
    text: string;
    href: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    href: string;
  };
  image: {
    src: string;
    alt: string;
  };
  stats?: Array<{
    value: string;
    label: string;
  }>;
  showStats?: boolean;
}

export function HeroSection({
  badge,
  headline,
  highlightedText,
  subheadline,
  primaryCTA,
  secondaryCTA,
  image,
  stats,
  showStats = true,
}: HeroSectionProps) {
  return (
    <section className="bg-background pt-18">
      <div className="mx-auto max-w-7xl px-6 py-12 lg:px-12 lg:py-20">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
          {/* Content */}
          <div className="space-y-8">
            {badge && (
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-semibold text-primary">
                {badge.icon}
                {badge.text}
              </div>
            )}

            <div className="space-y-6">
              <h1 className="text-5xl font-extrabold leading-[1.1] tracking-tight text-foreground lg:text-6xl xl:text-7xl">
                {headline}
                {highlightedText && <span className="block text-primary">{highlightedText}</span>}
              </h1>
              <p className="max-w-xl text-lg leading-[1.7] text-muted-foreground lg:text-xl">
                {subheadline}
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              {primaryCTA.onClick ? (
                <Button
                  size="lg"
                  className="h-14 px-8 text-base font-semibold"
                  onClick={primaryCTA.onClick}
                >
                  {primaryCTA.text} <ArrowRight className="ml-2 size-5" />
                </Button>
              ) : (
                <Button size="lg" className="h-14 px-8 text-base font-semibold" asChild>
                  <a href={primaryCTA.href}>
                    {primaryCTA.text} <ArrowRight className="ml-2 size-5" />
                  </a>
                </Button>
              )}
              {secondaryCTA && (
                <Button
                  size="lg"
                  variant="outline"
                  asChild
                  className="h-14 px-8 text-base font-semibold"
                >
                  <a href={secondaryCTA.href}>{secondaryCTA.text}</a>
                </Button>
              )}
            </div>

            {showStats && stats && stats.length > 0 && (
              <div className="grid grid-cols-3 gap-8 border-t pt-8">
                {stats.map((stat, index) => (
                  <div key={index}>
                    <div className="text-3xl font-bold text-foreground">{stat.value}</div>
                    <div className="mt-1 text-sm text-muted-foreground">{stat.label}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Image */}
          <div className="relative">
            <img src={image.src} alt={image.alt} className="h-auto w-full object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}
